import React from 'react';
import H from '@hero/ui-kit/typography/H';
import P from '@hero/ui-kit/typography/P';
import MarkModal from './MarkModal';
import { MarkButton } from './MarkControls';
import InlineLoader from '../../../components/InlineLoader';
import { SampleTable } from '../pages/Segments/SegmentBuilder/PreviewPanel';
import type { SegmentPreviewResponse, SegmentTargetKind } from '../types';
import useSegmentPreview from '../api/useSegmentPreview';
import { getSegmentErrorCode } from '../pages/Segments/segmentErrors';
import { SEGMENT_ERROR_LABELS } from '../constants';

type Props = {
    segmentName: string | null;
    description: string;
    targetKind: SegmentTargetKind | '';
    data: SegmentPreviewResponse | null;
    isLoading: boolean;
    error: string | null;
    onRefresh: () => void;
    onClose: () => void;
};

const AudiencePreviewModal: React.FC<Props> = ({
    segmentName,
    description,
    targetKind,
    data,
    isLoading,
    error,
    onRefresh,
    onClose
}) => (
    <MarkModal aria-label="Audience preview" onClose={onClose} styles={{ alignItems: 'stretch' }}>
        <header style={{ paddingRight: '3.2rem' }}>
            <H role="h4" styles={{ fontSize: 'clamp(2.4rem, 3vw, 3.2rem)' }}>
                Audience preview
            </H>
            <P noDefaultMargin>{segmentName ?? 'Selected audience segment'}</P>
        </header>
        <P noDefaultMargin size="small" styles={{ color: 'var(--mark-muted)' }}>
            {description}
        </P>
        {isLoading ? (
            <div role="status" aria-label="Loading audience preview">
                <InlineLoader />
            </div>
        ) : error ? (
            <P role="alert" noDefaultMargin styles={{ color: 'var(--mark-danger)' }}>
                {error}
            </P>
        ) : data ? (
            <div aria-live="polite" style={{ minWidth: 0 }}>
                <H role="h5" styles={{ marginBottom: '0.4rem' }}>
                    {`${data.match_count.toLocaleString()} matching records`}
                </H>
                {data.sample.length > 0 ? (
                    <>
                        <P noDefaultMargin size="small">
                            Sample of up to 10 matches
                        </P>
                        <SampleTable
                            sample={data.sample}
                            targetKind={targetKind}
                            showTechnical={false}
                        />
                    </>
                ) : (
                    <P noDefaultMargin size="small">
                        {data.match_count === 0
                            ? 'No records currently match this audience.'
                            : 'Matching records were found, but no sample is available.'}
                    </P>
                )}
            </div>
        ) : null}
        <div
            style={{ display: 'flex', flexWrap: 'wrap', gap: '1.2rem', justifyContent: 'flex-end' }}
        >
            <MarkButton onClick={onClose} variant="secondary">
                Close
            </MarkButton>
            <MarkButton onClick={onRefresh} disabled={isLoading}>
                {error ? 'Retry' : 'Refresh audience'}
            </MarkButton>
        </div>
    </MarkModal>
);

type PreviewButtonProps = {
    segmentId: number | null;
    segmentName: string | null;
    targetKind?: SegmentTargetKind | '';
    description?: string;
    disabled?: boolean;
};

const SegmentAudiencePreview: React.FC<PreviewButtonProps> = ({
    segmentId,
    segmentName,
    targetKind = '',
    description = 'This preview shows who matches the segment now. It is a sample, not a final recipient list.',
    disabled = false
}) => {
    const [open, setOpen] = React.useState(false);
    const preview = useSegmentPreview();
    const matchesSegment = preview.variables?.segment_id === segmentId;
    const errorCode = getSegmentErrorCode(preview.error);
    const error =
        matchesSegment && preview.isError
            ? (errorCode && SEGMENT_ERROR_LABELS[errorCode]) ||
              'Audience preview could not be loaded. Please retry.'
            : null;
    const requestPreview = () => {
        if (segmentId == null || disabled || preview.isPending) return;
        setOpen(true);
        preview.mutate({ segment_id: segmentId });
    };
    return (
        <>
            <MarkButton
                onClick={requestPreview}
                disabled={disabled || segmentId == null || preview.isPending}
                title={
                    segmentId == null ? 'Select an audience segment before previewing.' : undefined
                }
            >
                Preview audience
            </MarkButton>
            {open && segmentId != null && (
                <AudiencePreviewModal
                    segmentName={segmentName}
                    description={description}
                    targetKind={targetKind}
                    data={matchesSegment ? (preview.data?.data ?? null) : null}
                    isLoading={matchesSegment && preview.isPending}
                    error={error}
                    onRefresh={requestPreview}
                    onClose={() => setOpen(false)}
                />
            )}
        </>
    );
};

export default SegmentAudiencePreview;
